// notification-navigation.service.ts
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { NotificationsService } from "./notifications.service";

@Injectable({ providedIn: "root" })
export class NotificationNavigationService {
  constructor(
    private readonly router: Router,
    private readonly notificationService: NotificationsService,
    private readonly toastr: ToastrService
  ) {}

  routePage(Page: any, routeId: any) {
    let routepath: any = "";
    let queryParam: any = {};
    switch (Page) {
      case "proposal":
        routepath = `/getproposal/${btoa(routeId)}/proposal-overview`;
        break;
      case "opportunity":
        routepath = `/rfx/${btoa(routeId)}/opportunity/preview`;
        break;
      case "delivery":
        routepath = `/balancedscorecard/dashboard?project=` + btoa(routeId);
        break;
      case "bvt":
        routepath = "/BVT/list";
        break;
      case "okr":
        routepath = "/okrconvergence";
        break;
      case "workforcedashboard":
        routepath = `/workforce/view/` + btoa(routeId);
        break;
      case "assessmentinstance":
        routepath =
          `/strategicRelevance/assessment-central/assessment-result/` +
          btoa(routeId);
        break;
      case "activity":
      case "plan":
        routepath = `/confluence/plan/${btoa(routeId)}`;
        break;
      case "opportunityvendors":
        routepath = `/toe/${btoa(routeId)}/award-deal`;
        break;
      case "governancelighthouseinstance":
        routepath = `/strategicRelevance/governancect/assessment-result/${btoa(routeId)}`;
        break;
      case "auditinstance":
        routepath = `/incidentracker/instance`;
        queryParam = { id: routeId };
        break;
      case "risk":
        routepath = "risk";
        break;
      case "projectriskmitigation":
        let isRiskTeam = false;
        try {
          const perms = JSON.parse(localStorage.getItem("permission") || "{}");
          isRiskTeam = (perms.accessible_modules || []).includes(
            "module.risknet"
          );
        } catch (e) {}
        if (isRiskTeam) {
          routepath = "project-risks";
        } else {
          routepath = `/actiontracker`;
          queryParam = { type: "projectriskmitigation", id: routeId };
        }
        break;
      case "projectriskreviewcycle":
        routepath = "projectriskreviewcycle";
        break;
      case "projectrisks":
        routepath = "project-risks";
        break;
      case "audittemplate":
        routepath = "incidentracker/templates";
        break;
      default:
        routepath = "";
        break;
    }
    return { routepath, queryParam };
  }

  /**
   * Compliance notifications carry a server-assigned `nav_type` naming their destination,
   * because the content type alone is ambiguous — several destinations attach the same
   * model. These are resolved through `notification-helper`, which returns the exact ids
   * each route needs, so this handles them before the legacy content_type switch.
   *
   * `compliance_monitor_obligation_by_task` is the same destination as
   * `compliance_monitor_obligation`, keyed by a TaskInstance id rather than an obligation
   * id; the helper does the task -> obligation hop and answers with
   * `compliance_monitor_obligation`, so only this set needs to know the by-task type.
   */
  private readonly complianceNavTypes = new Set([
    "compliance_obligation_assigned",
    "compliance_monitor_obligation",
    "compliance_monitor_obligation_by_task",
    "compliance_obligation_task",
    "compliance_action",
  ]);

  navigate(notification: any, onDone?: () => void) {
    const navType = notification?.nav_type;
    if (navType && this.complianceNavTypes.has(navType)) {
      this.navigateCompliance(notification, navType, onDone);
      return;
    }

    const result = this.routePage(
      notification?.subject?.toLowerCase(),
      notification?.details?.id
    );
    notification["routepath"] = result?.routepath || "";
    notification["queryParams"] = result?.queryParam || {};

    if (notification.routepath === "risk") {
      const notificationId = notification.details?.id || notification.id;
      const qParams = `type=risk&id=${notificationId}`;
      this.notificationService.getnotificationHelper(qParams).subscribe({
        next: (res: any) => {
          this.router.navigate(["/riskconvergence/risk-register"], {
            queryParams: {
              type: "risk",
              domain_id: res.risk_domain_id,
              subdomain_id: res.risk_subdomain_id,
              risk_id: res.risk_id,
              domain_uuid: res.risk_domain_uuid,
              subdomain_uuid: res.risk_subdomain_uuid,
              risk_uuid: res.risk_uuid,
            },
          });
          onDone?.();
        },
        error: (_err) => {
          this.toastr.error("Could not fetch risk details");
          this.router.navigate(["/riskconvergence/risk-register"]);
          onDone?.();
        },
      });
    } else if (notification.routepath === "projectriskreviewcycle") {
      let qParams = `type=projectriskreviewcycle&id=${notification.details?.id}`;
      this.notificationService.getnotificationHelper(qParams).subscribe({
        next: (res: any) => {
          const projectId = res.project_id || res.projectId;
          if (projectId) {
            localStorage.setItem(
              "program",
              JSON.stringify({
                program_id: res.program_id,
                subportfolio_id: res.subportfolio_id,
                portfolio_id: res.portfolio_id,
                program_name: res.program_name,
                subportfolio_name: res.subportfolio_name,
                portfolio_name: res.portfolio_name,
              })
            );
            this.router.navigate([`/view/${btoa(projectId)}/risklog`], {
              queryParams: { activeTab: 2 },
            });
          } else {
            this.toastr.error("Could not fetch project");
          }
          onDone?.();
        },
        error: () => {
          this.toastr.error("Could not fetch project");
          onDone?.();
        },
      });
    } else if (notification.routepath === "project-risks") {
      let qParams = `type=projectrisks&id=${notification.details?.id}`;
      const subjectType = notification.subject?.toLowerCase();
      if (subjectType === "projectriskmitigation") {
        qParams = `type=projectriskmitigation&id=${notification.details?.id}&risk_team=True`;
      }
      this.notificationService.getnotificationHelper(qParams).subscribe({
        next: (res: any) => {
          const projectId = res.project_id || res.projectId;
          const riskId = res.risk_id || res.riskId || res.id;
          if (projectId) {
            localStorage.setItem(
              "program",
              JSON.stringify({
                program_id: res.program_id,
                subportfolio_id: res.subportfolio_id,
                portfolio_id: res.portfolio_id,
                program_name: res.program_name,
                subportfolio_name: res.subportfolio_name,
                portfolio_name: res.portfolio_name,
              })
            );
            let navigateParams: any = { activeTab: 2, targetRiskId: riskId };
            if (subjectType === "projectriskmitigation") {
              navigateParams.mitigationTab = "completed";
            }
            this.router.navigate([`/view/${btoa(projectId)}/risklog`], {
              queryParams: navigateParams,
            });
          } else {
            this.toastr.error("Could not fetch project");
          }
          onDone?.();
        },
        error: () => {
          this.toastr.error("Could not fetch project");
          onDone?.();
        },
      });
    } else if (notification.routepath) {
      this.router.navigate([notification.routepath], {
        queryParams: notification.queryParams,
      });
      onDone?.();
    } else {
      this.router.navigate(["/"]);
      onDone?.();
    }
  }

  /**
   * Resolve a compliance notification through `notification-helper` and route to the
   * destination its nav_type names.
   *
   * `nav_id` is the notification's raw object_id, which is what the helper expects — not
   * `details.id`, which the serializer rewrites for some subjects.
   */
  private navigateCompliance(
    notification: any,
    navType: string,
    onDone?: () => void
  ) {
    const refId = notification?.nav_id ?? notification?.details?.id;
    if (!refId) {
      this.toastr.error("This notification has no destination");
      onDone?.();
      return;
    }

    this.notificationService
      .getnotificationHelper(`type=${navType}&id=${refId}`)
      .subscribe({
        next: (res: any) => {
          this.routeComplianceTarget(res, onDone);
        },
        error: () => {
          this.toastr.error("Could not open this notification");
          onDone?.();
        },
      });
  }

  private routeComplianceTarget(res: any, onDone?: () => void) {
    switch (res?.nav_type) {
      case "compliance_obligation_assigned":
        this.goToObligationInDocument(res, onDone);
        break;
      case "compliance_monitor_obligation":
        this.goToComplianceMonitor(res, onDone);
        break;
      case "compliance_obligation_task":
        this.goToObligationTask(res, onDone);
        break;
      case "compliance_action":
        this.goToComplianceAction(res, onDone);
        break;
      default:
        this.toastr.error("Could not open this notification");
        onDone?.();
    }
  }

  /**
   * ComplianceDetailsComponent, listing the obligation's document filtered down to that
   * one obligation. The filter travels base64-encoded in `filter`, the same shape that
   * component already writes when it navigates away and restores on arrival — and it
   * matches on system_id.
   */
  private goToObligationInDocument(res: any, onDone?: () => void) {
    if (!res?.document_id) {
      // An obligation with no document has no ComplianceDetailsComponent to show it in.
      this.goToComplianceMonitor(res, onDone);
      return;
    }

    const queryParams: any = { id: res.document_id };
    if (res.obligation_system_id) {
      queryParams.filter = btoa(
        `obligation_system_id=${res.obligation_system_id}`
      );
    }

    this.router.navigate(["/incidentracker/compliance-details"], {
      queryParams,
    });
    onDone?.();
  }

  /** The Compliance Monitor tab (index 1), filtered to one obligation. */
  private goToComplianceMonitor(res: any, onDone?: () => void) {
    const queryParams: any = { tab: 1 };
    if (res?.obligation_system_id) {
      queryParams.filter = btoa(
        `obligation_system_id=${res.obligation_system_id}`
      );
    }

    this.router.navigate(["/incidentracker"], { queryParams });
    onDone?.();
  }

  /**
   * The obligation page with the marked control expanded and its task open in
   * ComplianceTaskComponent. ControlsComponent picks `controlId`/`taskId` off the query
   * string and opens the dialog itself.
   */
  private goToObligationTask(res: any, onDone?: () => void) {
    if (!res?.obligation_id || !res?.document_id) {
      this.goToComplianceMonitor(res, onDone);
      return;
    }

    this.router.navigate(["/incidentracker/clause-details"], {
      queryParams: {
        clauseId: res.obligation_id,
        documentId: res.document_id,
        controlId: res.control_id,
        taskId: res.task_id,
        source: "mapped-obligations",
      },
    });
    onDone?.();
  }

  /**
   * The action tracker, opening the action in ActionCreateModalDialogComponent.
   *
   * ListTrackerComponent resolves the action itself from `type`/`id` and picks the tab
   * from the action's own created_by, which is what keeps a self-review action — where
   * performer and reviewer are the same person, so the reviewer's action was created by
   * them — on "Actions created by you" rather than "collaborating on".
   */
  private goToComplianceAction(res: any, onDone?: () => void) {
    if (!res?.action_id) {
      this.goToComplianceMonitor(res, onDone);
      return;
    }

    this.router.navigate(["/actiontracker"], {
      queryParams: { type: "compliance_action", id: res.action_id },
    });
    onDone?.();
  }
}
